/**
 * Hook for converting between coordinate systems.
 *
 * Extend AI returns polygon coordinates in pixels relative to the document's
 * native resolution. This hook provides utilities to convert those to
 * percentage-based coordinates for rendering overlays.
 */

import { useCallback } from 'react';
import type {
  Citation,
  HighlightArea,
  PageDimensions,
  PolygonPoint,
} from '../types';

/**
 * Convert a polygon (pixel coordinates) to a highlight area (percentages).
 */
export function polygonToHighlightArea(
  polygon: PolygonPoint[],
  pageDimensions: PageDimensions,
  pageIndex: number,
): HighlightArea {
  if (polygon.length === 0) {
    return { left: 0, top: 0, width: 0, height: 0, pageIndex };
  }

  // Find bounding rectangle from polygon points
  const bounds = polygon.reduce(
    (acc, point) => ({
      left: Math.min(acc.left, point.x),
      top: Math.min(acc.top, point.y),
      right: Math.max(acc.right, point.x),
      bottom: Math.max(acc.bottom, point.y),
    }),
    {
      left: Infinity,
      top: Infinity,
      right: -Infinity,
      bottom: -Infinity,
    },
  );

  const { width: pageWidth, height: pageHeight } = pageDimensions;

  // Convert to percentages
  // Adding small padding (1%) for visual clarity as recommended by Extend AI docs
  return {
    left: Math.max(0, (bounds.left / pageWidth) * 100 - 0.5),
    top: Math.max(0, (bounds.top / pageHeight) * 100 - 0.5),
    width: Math.min(100, ((bounds.right - bounds.left) / pageWidth) * 100 + 1),
    height: Math.min(
      100,
      ((bounds.bottom - bounds.top) / pageHeight) * 100 + 1,
    ),
    pageIndex,
  };
}

/**
 * Convert polygon points from pixels to percentages.
 */
export function polygonToPercentages(
  polygon: PolygonPoint[],
  pageDimensions: PageDimensions,
): PolygonPoint[] {
  const { width: pageWidth, height: pageHeight } = pageDimensions;

  return polygon.map((point) => ({
    x: (point.x / pageWidth) * 100,
    y: (point.y / pageHeight) * 100,
  }));
}

/**
 * Scale percentage-based coordinates to actual pixel positions.
 */
export function percentageToPixels(
  percentX: number,
  percentY: number,
  containerWidth: number,
  containerHeight: number,
): { x: number; y: number } {
  return {
    x: (percentX / 100) * containerWidth,
    y: (percentY / 100) * containerHeight,
  };
}

/**
 * Hook providing coordinate conversion utilities.
 */
export function useCoordinateConversion() {
  const citationToHighlightArea = useCallback(
    (
      citation: Citation,
      pageDimensions: PageDimensions | undefined,
    ): HighlightArea | null => {
      if (
        !pageDimensions ||
        !citation.polygon ||
        citation.polygon.length === 0
      ) {
        return null;
      }

      // Page index is 0-based for rendering, but citation.page is 1-based
      const pageIndex = citation.page - 1;

      return polygonToHighlightArea(
        citation.polygon,
        pageDimensions,
        pageIndex,
      );
    },
    [],
  );

  const citationsToHighlightAreas = useCallback(
    (
      citations: Citation[],
      pageDimensions: Record<number, PageDimensions>,
    ): HighlightArea[] => {
      return citations
        .map((citation) => {
          const dims = pageDimensions[citation.page];
          if (!dims) return null;
          return citationToHighlightArea(citation, dims);
        })
        .filter((area): area is HighlightArea => area !== null);
    },
    [citationToHighlightArea],
  );

  return {
    polygonToHighlightArea,
    polygonToPercentages,
    percentageToPixels,
    citationToHighlightArea,
    citationsToHighlightAreas,
  };
}
